import _get from 'lodash/get';
import { DocumentationBulkEditInputObject } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import { GetTaskGroupKey } from '../../utils/createTaskGroupKeyResolver';
import buildTaskGroupLinks from '../../utils/buildTaskGroupLinks';
import resolveCategoryPermissionOverride, {
  CategoryOption,
} from '../../utils/resolveCategoryPermissionOverride';

type SelectOption = { value: string; label: string };
type LinkOption = SelectOption & { visibleViaQr?: boolean };

export default function prepareBulkEditData(
  values: Values,
  getTaskGroupKey?: GetTaskGroupKey,
): DocumentationBulkEditInputObject {
  const attributes: DocumentationBulkEditInputObject = {};

  const category = _get(values, DocumentFormFieldNames.Category) as
    CategoryOption | undefined;
  if (category?.value) {
    attributes.categoryId = category.value;
  }

  // "Allow download"/"Allow printing" checkboxes, prefilled from the
  // selected category (see getCategoryFields.tsx). The bulk-edit form has no
  // defaults (defaultValues={{}}), so these stay `undefined` unless the admin
  // picks a category (which syncs them to its defaults) or toggles a
  // checkbox directly — either way, only a touched value is sent, so
  // untouched documents keep whatever permissions they already had.
  const allowDownloadRaw = _get(
    values,
    DocumentFormFieldNames.AllowDownload,
  ) as boolean | undefined;
  const allowPrintRaw = _get(values, DocumentFormFieldNames.AllowPrint) as
    boolean | undefined;
  if (allowDownloadRaw !== undefined) {
    attributes.allowDownload = resolveCategoryPermissionOverride(
      category,
      'allowDownload',
      allowDownloadRaw,
    );
  }
  if (allowPrintRaw !== undefined) {
    attributes.allowPrint = resolveCategoryPermissionOverride(
      category,
      'allowPrint',
      allowPrintRaw,
    );
  }

  const sectors = _get(values, DocumentFormFieldNames.Sectors) as
    SelectOption[] | undefined;
  if (sectors?.length) {
    attributes.sectorLinks = sectors.map((s) => ({ id: s.value }));
  }

  const sites = _get(values, DocumentFormFieldNames.Sites) as
    SelectOption[] | undefined;
  if (sites?.length) {
    attributes.siteLinks = sites.map((s) => ({ id: s.value }));
  }

  const approver = _get(values, DocumentFormFieldNames.Approver) as
    SelectOption | undefined;
  if (approver?.value) {
    attributes.approverId = approver.value;
  }

  const equipment = _get(values, DocumentFormFieldNames.Equipment) as
    LinkOption[] | undefined;
  if (equipment?.length) {
    attributes.assetLinks = equipment.map((e) => ({
      id: e.value,
      visibleViaQr: e.visibleViaQr ?? true,
    }));
  }

  const areas = _get(values, DocumentFormFieldNames.Areas) as
    LinkOption[] | undefined;
  if (areas?.length) {
    attributes.areaLinks = areas.map((a) => ({
      id: a.value,
      visibleViaQr: a.visibleViaQr ?? true,
    }));
  }

  const formTemplates = _get(values, DocumentFormFieldNames.FormTemplates) as
    SelectOption[] | undefined;
  if (formTemplates?.length) {
    attributes.formTemplateLinks = formTemplates.map((f) => ({ id: f.value }));
  }

  const taskGroupLinks = buildTaskGroupLinks({
    values: _get(values, DocumentFormFieldNames.Tasks) as
      SelectOption[] | undefined,
    getTaskGroupKey,
  });
  if (taskGroupLinks) attributes.taskGroupLinks = taskGroupLinks;

  return attributes;
}
